import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ── TextMotion: kinetic type presets on GSAP ──
// 37 presets: token splitting (character / word / line), 3D transforms, SVG turbulence, scramble & typewriter.
// Markup: <p data-text-motion="fade">…</p> (optional data-trigger, data-delay, data-duration, data-stagger,
// data-intensity, data-split-by, data-order, data-easing, data-replay). Plain text only: inline markup is flattened.

export type TextMotionPreset =
  | 'fade' | 'rise' | 'drop' | 'slide' | 'split' | 'blur' | 'focus' | 'mask'
  | 'curtain' | 'unfold' | 'wipe' | 'roll' | 'pop' | 'punch' | 'zoom' | 'shrink'
  | 'stretch' | 'flip' | 'swivel' | 'tilt' | 'spin' | 'shear' | 'glitch' | 'wave'
  | 'cascade' | 'scatter' | 'scramble' | 'type' | 'liquid' | 'dissolve' | 'chroma'
  | 'shine' | 'neon' | 'explode' | 'vortex' | 'warp' | 'melt';

export type SplitBy = 'character' | 'word' | 'line';
export type Order = 'forward' | 'reverse' | 'center' | 'random';
export type EasingName = 'smooth' | 'snappy' | 'gentle' | 'spring' | 'bounce';
export type Origin = 'center' | 'top' | 'bottom' | 'left' | 'right';
export type Trigger = 'onView' | 'onMount' | 'hover' | 'click' | 'loop' | 'onScroll';

export interface TextMotionConfig {
  splitBy: SplitBy;
  effect: string;
  amount: number;
  detail: number;
  settle: number;
  chaos: number;
  z: number;
  x: number;
  y: number;
  opacity: number;
  blur: number;
  scale: number;
  scaleX: number;
  scaleY: number;
  rotate: number;
  rotateX: number;
  rotateY: number;
  skewX: number;
  origin: Origin;
  clip: boolean;
  order: Order;
  duration: number;
  stagger: number;
  easing: EasingName;
}

export const BASE: TextMotionConfig = {
  splitBy: 'word',
  effect: 'transform',
  amount: 0,
  detail: 10,
  settle: 1.8,
  chaos: 0,
  z: 0,
  x: 0,
  y: 0,
  opacity: 0,
  blur: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  rotate: 0,
  rotateX: 0,
  rotateY: 0,
  skewX: 0,
  origin: 'center',
  clip: false,
  order: 'forward',
  duration: 0.7,
  stagger: 0.03,
  easing: 'smooth',
};

export const PRESETS: Record<TextMotionPreset, Partial<TextMotionConfig>> = {
  // Movement
  fade: { y: 18, duration: 0.6, stagger: 0.04 },
  rise: { splitBy: 'line', y: 80, duration: 0.8, stagger: 0.12, easing: 'spring' },
  drop: { splitBy: 'character', y: -60, duration: 0.7, stagger: 0.02, easing: 'spring' },
  slide: { x: -60, duration: 0.7, stagger: 0.05 },
  // Softness
  split: { splitBy: 'character', y: 48, blur: 8, duration: 0.7, stagger: 0.02, easing: 'spring' },
  blur: { blur: 14, duration: 0.9, stagger: 0.06 },
  focus: { splitBy: 'character', blur: 18, scale: 1.15, duration: 0.8, stagger: 0.02 },
  // Reveal
  mask: { splitBy: 'line', clip: true, y: 100, duration: 0.9, stagger: 0.12, easing: 'snappy' },
  curtain: { clip: true, y: 100, duration: 0.7, stagger: 0.05, easing: 'snappy' },
  unfold: { splitBy: 'line', rotateX: -80, origin: 'top', duration: 0.9, stagger: 0.1, easing: 'gentle' },
  wipe: { splitBy: 'line', effect: 'wipe', amount: 22, opacity: 1, duration: 0.9, stagger: 0.12, easing: 'snappy' },
  roll: { splitBy: 'character', clip: true, y: 100, rotateX: -90, origin: 'bottom', duration: 0.7, stagger: 0.03, easing: 'snappy' },
  // Scale
  pop: { splitBy: 'character', scale: 0.4, duration: 0.6, stagger: 0.025, easing: 'snappy' },
  punch: { splitBy: 'line', scale: 0.8, y: 20, duration: 0.5, stagger: 0.08, easing: 'bounce' },
  zoom: { splitBy: 'line', scale: 1.6, blur: 10, duration: 0.9, stagger: 0.1 },
  shrink: { splitBy: 'character', scale: 2.2, duration: 0.6, stagger: 0.02, easing: 'snappy' },
  stretch: { splitBy: 'character', scaleX: 0.3, scaleY: 1.8, duration: 0.7, stagger: 0.025, easing: 'spring' },
  // Rotation
  flip: { rotateX: 90, duration: 0.8, stagger: 0.06, easing: 'gentle' },
  swivel: { rotateY: 90, duration: 0.8, stagger: 0.06, easing: 'gentle' },
  tilt: { splitBy: 'character', rotate: -12, y: 30, duration: 0.7, stagger: 0.025, easing: 'spring' },
  spin: { splitBy: 'character', rotate: 180, scale: 0.5, duration: 0.7, stagger: 0.03, easing: 'snappy' },
  shear: { skewX: 20, x: 30, duration: 0.6, stagger: 0.05, easing: 'snappy' },
  // Sequencing
  glitch: { splitBy: 'character', x: 14, skewX: -18, duration: 0.35, stagger: 0.012, easing: 'snappy', order: 'random' },
  wave: { splitBy: 'character', y: 30, duration: 0.7, stagger: 0.035, easing: 'spring', order: 'center' },
  cascade: { splitBy: 'character', y: 40, duration: 0.7, stagger: 0.025, order: 'reverse' },
  scatter: { splitBy: 'character', y: 40, x: 20, rotate: 20, duration: 0.8, stagger: 0.03, easing: 'spring', order: 'random' },
  // Signature
  scramble: { splitBy: 'character', effect: 'scramble', opacity: 1, duration: 0.4, stagger: 0.035 },
  type: { splitBy: 'character', effect: 'type', opacity: 1, duration: 0.01, stagger: 0.045 },
  liquid: { splitBy: 'line', effect: 'liquid', amount: 70, detail: 10, settle: 1.3, duration: 2.4, stagger: 0.18 },
  dissolve: { effect: 'dissolve', amount: 150, detail: 76, settle: 2.2, duration: 1.2, stagger: 0.06 },
  chroma: { splitBy: 'character', effect: 'chroma', amount: 14, duration: 0.7, stagger: 0.02, easing: 'snappy' },
  shine: { splitBy: 'character', effect: 'shine', amount: 2.8, duration: 0.5, stagger: 0.03 },
  neon: { effect: 'neon', amount: 18, duration: 0.9, stagger: 0.07 },
  // Extravagant
  explode: { splitBy: 'character', chaos: 1, scale: 0.3, blur: 4, duration: 1.1, stagger: 0.015, easing: 'spring', order: 'random' },
  vortex: { splitBy: 'character', chaos: 0.55, rotate: 720, scale: 0, duration: 1.2, stagger: 0.03, easing: 'spring' },
  warp: { z: -900, rotateY: 65, blur: 12, duration: 1, stagger: 0.08, origin: 'left' },
  melt: { splitBy: 'line', effect: 'melt', amount: 95, detail: 6, settle: 1.2, duration: 2.3, stagger: 0.15 },
};

const ORIGIN_MAP: Record<Origin, string> = {
  center: '50% 50%',
  top: '50% 0%',
  bottom: '50% 100%',
  left: '0% 50%',
  right: '100% 50%',
};

const EASING_MAP: Record<EasingName, string> = {
  spring: 'power2.out',
  bounce: 'bounce.out',
  smooth: 'power3.out',
  snappy: 'power4.out',
  gentle: 'power2.inOut',
};

const SCRAMBLE_GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&@$*<>/\\/[]{}';

function pseudoRandom(n: number): number {
  const x = Math.sin((n + 1) * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function staggerIndex(i: number, total: number, order: Order): number {
  switch (order) {
    case 'reverse': return total - 1 - i;
    case 'center': return Math.abs(i - (total - 1) / 2);
    case 'random': return pseudoRandom(i) * total;
    default: return i;
  }
}

function spread(seed: number, salt: number): number {
  return (pseudoRandom(seed * 3 + salt) - 0.5) * 2;
}

function detailToFrequency(detail: number, effect: string): string {
  const f = Math.max(Math.pow(detail / 100, 2) * 1.2, 5e-4);
  if (effect === 'liquid') return `${f.toFixed(4)} ${(f * 1.8).toFixed(4)}`;
  if (effect === 'melt') return `${(f * 0.25).toFixed(4)} ${(f * 6).toFixed(4)}`;
  return `${f.toFixed(4)} ${f.toFixed(4)}`;
}

// Split in the palette's two accents (lime / blue), never ad-hoc colours.
function chromaShadow(offset: number, alpha: number): string {
  return `${offset}px 0 0 rgba(var(--lime-rgb), ${alpha}), ${-offset}px 0 0 rgba(var(--volt-rgb), ${alpha})`;
}

function wipeClip(progress: number, skew: number): string {
  return progress === 0
    ? `polygon(0% 0%, 0% 0%, ${-skew}% 100%, 0% 100%)`
    : `polygon(0% 0%, ${100 + skew}% 0%, 100% 100%, 0% 100%)`;
}

export interface TextMotionOptions extends Partial<TextMotionConfig> {
  preset?: TextMotionPreset | 'custom';
  trigger?: Trigger;
  delay?: number;
  intensity?: number;
  threshold?: number;
  replay?: boolean;
}

export interface TextMotionInstance {
  play: () => void;
  reset: () => void;
  kill: () => void;
}

let filterSeq = 0;

/** Split `el` into tokens and animate them with a preset (or custom values). */
export function textMotion(el: HTMLElement, opts: TextMotionOptions = {}): TextMotionInstance {
  const presetKey = opts.preset ?? 'split';
  const basePreset = presetKey === 'custom'
    ? { ...BASE, splitBy: 'character' as SplitBy, y: 40, easing: 'spring' as EasingName }
    : { ...BASE, ...(PRESETS[presetKey as TextMotionPreset] ?? {}) };

  const intensity = opts.intensity ?? 1;
  const ratio = (v: number) => 1 + (v - 1) * intensity;

  const m: TextMotionConfig = {
    splitBy: opts.splitBy ?? basePreset.splitBy,
    effect: opts.effect ?? basePreset.effect,
    amount: (opts.amount ?? basePreset.amount) * intensity,
    detail: opts.detail ?? basePreset.detail,
    settle: opts.settle ?? basePreset.settle,
    chaos: (opts.chaos ?? basePreset.chaos) * intensity,
    z: (opts.z ?? basePreset.z) * intensity,
    x: (opts.x ?? basePreset.x) * intensity,
    y: (opts.y ?? basePreset.y) * intensity,
    opacity: opts.opacity ?? basePreset.opacity,
    blur: (opts.blur ?? basePreset.blur) * intensity,
    scale: ratio(opts.scale ?? basePreset.scale),
    scaleX: ratio(opts.scaleX ?? basePreset.scaleX),
    scaleY: ratio(opts.scaleY ?? basePreset.scaleY),
    rotate: (opts.rotate ?? basePreset.rotate) * intensity,
    rotateX: (opts.rotateX ?? basePreset.rotateX) * intensity,
    rotateY: (opts.rotateY ?? basePreset.rotateY) * intensity,
    skewX: (opts.skewX ?? basePreset.skewX) * intensity,
    origin: opts.origin ?? basePreset.origin,
    clip: opts.clip ?? basePreset.clip,
    order: opts.order ?? basePreset.order,
    duration: opts.duration ?? basePreset.duration,
    stagger: opts.stagger ?? basePreset.stagger,
    easing: opts.easing ?? basePreset.easing,
  };

  const trigger: Trigger = opts.trigger ?? 'onView';
  const delay = opts.delay ?? 0;
  const threshold = opts.threshold ?? 0.35;
  const replay = opts.replay ?? false;

  // Preserve original text for reset
  const rawText = el.textContent || '';
  const lines = rawText.split('\n');

  // Tokenization
  el.innerHTML = '';
  el.style.transformStyle = 'preserve-3d';
  el.style.perspective = '800px';

  interface TokenMeta {
    el: HTMLElement;
    char: string;
    index: number;
    seed: number;
    orderIndex: number;
  }

  const tokens: TokenMeta[] = [];
  let runningIndex = 0;

  // Turbulence Filter Setup for liquid/dissolve/melt
  let svgFilter: SVGSVGElement | null = null;
  let displaceMap: SVGFEDisplacementMapElement | null = null;
  const isTurbulence = m.effect === 'liquid' || m.effect === 'dissolve' || m.effect === 'melt';
  const filterId = `tm-filter-${++filterSeq}`;

  if (isTurbulence) {
    svgFilter = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgFilter.setAttribute('width', '0');
    svgFilter.setAttribute('height', '0');
    svgFilter.style.position = 'absolute';
    svgFilter.style.pointerEvents = 'none';

    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttribute('id', filterId);

    const turb = document.createElementNS('http://www.w3.org/2000/svg', 'feTurbulence');
    turb.setAttribute('type', 'fractalNoise');
    turb.setAttribute('baseFrequency', detailToFrequency(m.detail, m.effect));
    turb.setAttribute('numOctaves', '2');
    turb.setAttribute('result', 'noise');

    displaceMap = document.createElementNS('http://www.w3.org/2000/svg', 'feDisplacementMap');
    displaceMap.setAttribute('in', 'SourceGraphic');
    displaceMap.setAttribute('in2', 'noise');
    displaceMap.setAttribute('scale', String(m.amount));
    displaceMap.setAttribute('xChannelSelector', 'R');
    displaceMap.setAttribute('yChannelSelector', 'G');

    filter.append(turb, displaceMap);
    svgFilter.append(filter);
    el.prepend(svgFilter);
    el.style.filter = `url(#${filterId})`;
  }

  // Build DOM tokens
  lines.forEach((lineText, li) => {
    const lineSpan = document.createElement('span');
    lineSpan.className = 'tm-line';
    lineSpan.style.display = 'block';
    lineSpan.style.transformStyle = 'preserve-3d';

    if (m.splitBy === 'line') {
      const idx = runningIndex++;
      const span = document.createElement('span');
      span.className = 'tm-token';
      span.textContent = lineText;
      span.style.display = 'inline-block';
      span.style.transformOrigin = ORIGIN_MAP[m.origin];
      span.style.willChange = 'transform, opacity, filter';
      tokens.push({ el: span, char: lineText, index: idx, seed: idx, orderIndex: idx });
      lineSpan.append(span);
    } else {
      const words = lineText.split(/(\s+)/).filter((t) => t.length > 0);
      words.forEach((word) => {
        if (/^\s+$/.test(word)) {
          lineSpan.append(document.createTextNode(word));
          return;
        }

        const wordSpan = document.createElement('span');
        wordSpan.className = 'tm-word';
        wordSpan.style.display = 'inline-block';
        wordSpan.style.transformStyle = 'preserve-3d';

        if (m.splitBy === 'character') {
          Array.from(word).forEach((ch) => {
            const idx = runningIndex++;
            const charSpan = document.createElement('span');
            charSpan.className = 'tm-token';
            charSpan.textContent = ch;
            charSpan.style.display = 'inline-block';
            charSpan.style.transformOrigin = ORIGIN_MAP[m.origin];
            charSpan.style.willChange = 'transform, opacity, filter';

            if (m.clip) {
              const clipBox = document.createElement('span');
              clipBox.className = 'tm-clip';
              clipBox.style.display = 'inline-block';
              clipBox.style.overflow = 'hidden';
              clipBox.style.verticalAlign = 'bottom';
              clipBox.style.paddingBottom = '0.15em';
              clipBox.style.marginBottom = '-0.15em';
              clipBox.append(charSpan);
              wordSpan.append(clipBox);
            } else {
              wordSpan.append(charSpan);
            }

            tokens.push({ el: charSpan, char: ch, index: idx, seed: idx, orderIndex: idx });
          });
        } else {
          // splitBy === 'word'
          const idx = runningIndex++;
          const tokenSpan = document.createElement('span');
          tokenSpan.className = 'tm-token';
          tokenSpan.textContent = word;
          tokenSpan.style.display = 'inline-block';
          tokenSpan.style.transformOrigin = ORIGIN_MAP[m.origin];
          tokenSpan.style.willChange = 'transform, opacity, filter';

          if (m.clip) {
            const clipBox = document.createElement('span');
            clipBox.className = 'tm-clip';
            clipBox.style.display = 'inline-block';
            clipBox.style.overflow = 'hidden';
            clipBox.style.verticalAlign = 'bottom';
            clipBox.style.paddingBottom = '0.15em';
            clipBox.style.marginBottom = '-0.15em';
            clipBox.append(tokenSpan);
            wordSpan.append(clipBox);
          } else {
            wordSpan.append(tokenSpan);
          }

          tokens.push({ el: tokenSpan, char: word, index: idx, seed: idx, orderIndex: idx });
        }
        lineSpan.append(wordSpan);
      });
    }
    el.append(lineSpan);
  });

  const total = Math.max(tokens.length, 1);
  tokens.forEach((t) => {
    t.orderIndex = staggerIndex(t.index, total, m.order);
  });

  // GSAP Timeline and Initial Pose
  let tl: gsap.core.Timeline | null = null;
  let clock: (() => void) | null = null; // per-frame content effect, on the shared GSAP ticker
  const stopClock = () => { if (clock) gsap.ticker.remove(clock); clock = null; };
  let scrollTrigger: ScrollTrigger | null = null;

  const setInitialPose = () => {
    tokens.forEach((t) => {
      const chaosX = m.chaos && !m.clip ? spread(t.seed, 1) * m.chaos * 240 : 0;
      const chaosY = m.chaos ? spread(t.seed, 2) * m.chaos * 200 : 0;
      const chaosRot = m.chaos ? spread(t.seed, 3) * m.chaos * 160 : 0;

      gsap.set(t.el, {
        opacity: m.opacity,
        x: m.x + chaosX,
        yPercent: m.clip ? m.y : 0,
        y: m.clip ? 0 : m.y + chaosY,
        z: m.z,
        scale: m.scale,
        scaleX: m.scaleX,
        scaleY: m.scaleY,
        rotation: m.rotate + chaosRot,
        rotationX: m.rotateX,
        rotationY: m.rotateY,
        skewX: m.skewX,
        transformPerspective: 800,
        filter: m.blur > 0 ? `blur(${m.blur}px)` : 'none',
      });

      if (m.effect === 'chroma') {
        t.el.style.textShadow = chromaShadow(m.amount, 0.9);
      }
      if (m.effect === 'wipe') {
        t.el.style.clipPath = wipeClip(0, m.amount);
      }
      if (m.effect === 'type') {
        t.el.textContent = '';
      }
    });

    if (displaceMap) {
      displaceMap.setAttribute('scale', String(m.amount));
    }
  };

  const play = () => {
    if (tl) tl.kill();
    stopClock();

    setInitialPose();
    tl = gsap.timeline();

    const ease = EASING_MAP[m.easing] || 'power3.out';

    tokens.forEach((t) => {
      const tokenDelay = delay + t.orderIndex * m.stagger;

      tl!.to(
        t.el,
        {
          opacity: 1,
          x: 0,
          y: 0,
          yPercent: 0,
          z: 0,
          scale: 1,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          rotationX: 0,
          rotationY: 0,
          skewX: 0,
          filter: 'blur(0px)',
          duration: m.duration,
          ease,
          onStart: () => {
            if (m.effect === 'chroma') {
              gsap.to(t.el, { textShadow: chromaShadow(0, 0), duration: m.duration, ease });
            }
            if (m.effect === 'wipe') {
              t.el.style.clipPath = wipeClip(1, m.amount);
            }
          },
        },
        tokenDelay
      );
    });

    // Content effects: Scramble / Typewriter / Turbulence clock
    const isClock = m.effect === 'scramble' || m.effect === 'type' || isTurbulence;
    if (isClock) {
      const startTime = performance.now();
      const totalTime = delay + m.duration + m.stagger * total;

      clock = () => {
        const elapsed = (performance.now() - startTime) / 1000;

        if (displaceMap) {
          const p = Math.min(elapsed / Math.max(totalTime, 0.01), 1);
          const eased = Math.pow(1 - p, m.settle);
          displaceMap.setAttribute('scale', String(m.amount * eased));
        }

        tokens.forEach((t) => {
          const revealAt = delay + t.orderIndex * m.stagger;
          if (elapsed >= revealAt) {
            t.el.textContent = t.char;
          } else if (m.effect === 'type') {
            t.el.textContent = '';
          } else if (m.effect === 'scramble') {
            const seed = Math.floor(elapsed / 0.045) + t.orderIndex;
            t.el.textContent = SCRAMBLE_GLYPHS[Math.floor(pseudoRandom(seed) * SCRAMBLE_GLYPHS.length)];
          }
        });

        if (elapsed >= totalTime + 0.1) {
          tokens.forEach((t) => { t.el.textContent = t.char; });
          if (displaceMap) displaceMap.setAttribute('scale', '0');
          stopClock();
        }
      };
      gsap.ticker.add(clock);
    }
  };

  const reset = () => {
    if (tl) tl.kill();
    stopClock();
    setInitialPose();
  };

  const kill = () => {
    if (tl) tl.kill();
    stopClock();
    if (scrollTrigger) scrollTrigger.kill();
    if (svgFilter) svgFilter.remove();
    el.textContent = rawText;
  };

  // Trigger setup
  setInitialPose();

  if (trigger === 'onMount') {
    play();
  } else if (trigger === 'onView') {
    scrollTrigger = ScrollTrigger.create({
      trigger: el,
      start: `top ${Math.round((1 - threshold) * 100)}%`,
      once: !replay,
      onEnter: play,
      onEnterBack: replay ? play : undefined,
    });
  } else if (trigger === 'hover') {
    el.addEventListener('mouseenter', play);
  } else if (trigger === 'click') {
    el.addEventListener('click', play);
  } else if (trigger === 'loop') {
    play();
    const cycleMs = (delay + m.duration + m.stagger * total + 1.2) * 1000;
    const interval = window.setInterval(play, cycleMs);
    const origKill = kill;
    return { play, reset, kill: () => { clearInterval(interval); origKill(); } };
  }

  return { play, reset, kill };
}

/**
 * Scan DOM for [data-text-motion] and initialize animations automatically.
 */
export function initTextMotion(root: ParentNode = document): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const elements = [...root.querySelectorAll<HTMLElement>('[data-text-motion], [data-motion]')];

  elements.forEach((el) => {
    const preset = (el.dataset.textMotion || el.dataset.motion || 'fade') as TextMotionPreset;
    const trigger = (el.dataset.trigger || 'onView') as Trigger;
    const delay = el.dataset.delay ? parseFloat(el.dataset.delay) : undefined;
    const duration = el.dataset.duration ? parseFloat(el.dataset.duration) : undefined;
    const stagger = el.dataset.stagger ? parseFloat(el.dataset.stagger) : undefined;
    const intensity = el.dataset.intensity ? parseFloat(el.dataset.intensity) : undefined;
    const splitBy = el.dataset.splitBy as SplitBy | undefined;
    const order = el.dataset.order as Order | undefined;
    const easing = el.dataset.easing as EasingName | undefined;
    const replay = el.dataset.replay === 'true';

    textMotion(el, {
      preset,
      trigger,
      delay,
      duration,
      stagger,
      intensity,
      splitBy,
      order,
      easing,
      replay,
    });
  });
}
